import { ExternalLink, BookOpen } from 'lucide-react';
import type { DataPointMap } from '@/hooks/useDataPointMeta';

interface SourceAttributionProps {
  dataPoints: DataPointMap;
  brandName: string;
  modelName: string;
  variantName: string;
  rangeWltpKm: number | null;
  batteryKwh: number | null;
  consumptionWhKm: number | null;
  chargingDcKw: number | null;
  priceDkk: number | null;
}

function sourceLink(label: string | null, url: string | null) {
  if (!label) return null;
  return url ? (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
    >
      {label}
      <ExternalLink className="h-3 w-3" />
    </a>
  ) : (
    <span className="font-medium text-foreground">{label}</span>
  );
}

/**
 * Kildeattribution vævet ind i prosaen på variantsiden.
 * Hver sætning bygges kun når både tallet og en registreret kilde findes —
 * der formuleres ingen sætninger uden data bag.
 */
export function SourceAttribution({
  dataPoints,
  brandName,
  modelName,
  variantName,
  rangeWltpKm,
  batteryKwh,
  consumptionWhKm,
  chargingDcKw,
  priceDkk,
}: SourceAttributionProps) {
  const carName = `${brandName} ${modelName} ${variantName}`;

  const sentences: { key: string; node: React.ReactNode }[] = [];

  const build = (
    field: string,
    value: number | null,
    render: (link: React.ReactNode, value: number) => React.ReactNode
  ) => {
    const meta = dataPoints.get(field);
    if (!meta?.sourceLabel || value == null) return;
    const link = sourceLink(meta.sourceLabel, meta.sourceUrl);
    sentences.push({ key: field, node: render(link, value) });
  };

  build('range_wltp_km', rangeWltpKm, (link, v) => (
    <>
      Ifølge {link} er den officielle WLTP-rækkevidde for {carName}{' '}
      <span className="font-semibold text-foreground">{v.toLocaleString('da-DK')} km</span>.
    </>
  ));

  build('battery_kwh', batteryKwh, (link, v) => (
    <>
      Batteriets nettokapacitet er oplyst til{' '}
      <span className="font-semibold text-foreground">
        {v.toLocaleString('da-DK', { maximumFractionDigits: 1 })} kWh
      </span>{' '}
      af {link}.
    </>
  ));

  build('consumption_wh_km', consumptionWhKm, (link, v) => (
    <>
      Det målte WLTP-elforbrug er{' '}
      <span className="font-semibold text-foreground">{v.toLocaleString('da-DK')} Wh/km</span> ifølge {link}.
    </>
  ));

  build('charging_dc_kw', chargingDcKw, (link, v) => (
    <>
      Maksimal DC-ladeeffekt er angivet til{' '}
      <span className="font-semibold text-foreground">{v.toLocaleString('da-DK')} kW</span> af {link}.
    </>
  ));

  build('price_dkk', priceDkk, (link, v) => (
    <>
      Den vejledende pris på{' '}
      <span className="font-semibold text-foreground">{v.toLocaleString('da-DK')} kr.</span> stammer fra {link}.
    </>
  ));

  if (sentences.length === 0) return null;

  return (
    <section className="mt-6 rounded-xl border border-border bg-card p-4 md:p-5">
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
        <BookOpen className="h-4 w-4 text-primary" />
        Hvor kommer tallene fra?
      </h2>
      <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">
        {sentences.map((s) => (
          <p key={s.key}>{s.node}</p>
        ))}
      </div>
    </section>
  );
}
